import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { EDITOR_TOOLS } from '@editor/config/constants'
import { CursorState } from '@editor/config/constants/cursor'
import { EditorContext } from '@editor/contexts/Editor'
import { useGlobalModalContext } from '@editor/contexts/GlobalModal'
import { PointArray } from '@editor/interfaces/attribute'
import { LineStyle, PolygonPlaneMeshProps } from '@editor/interfaces/canvas'
import { CanvasConfig, CanvasEventsExtra } from '@editor/interfaces/editor'
import { Polygon } from '@editor/interfaces/shape'
import { PointCloudProps } from '@editor/pages/projects/editor/objects/PointCloud'
import { prewarmTextureSampling, releaseTextureSampling } from '@editor/services/Color'
import { MinimumBoundingBox } from '@editor/services/MinimumBoundingBox'
import { findCenter } from '@editor/services/Points'
import { isPointInPolygon } from '@editor/services/Polygon'
import {
  DbScan,
  Decimate,
  DensityFilter,
  Linking,
  NoiseFilter,
  PolylineExtraction,
  Smoothing,
} from '@editor/services/PolylineExtraction'
import { alignToZAxis, generateDummyMinimumRectBoundary } from '@editor/services/Shape'
import { addBox3Helper, addWireframeBox, clearHelper } from '@editor/services/debug'
import { GenericError } from '@editor/services/errors/GenericError'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { useToolUtilSelector } from '@editor/store/selectors'
import { setCursor, toggleHiddenElementIds } from '@editor/store/tool'
import { CircleAnchorIcon, MeshTile, MeshTileDebug, getTileWorldBoundingBox } from '@infraloop/common'
import { ThreeEvent } from '@react-three/fiber'
import * as Sentry from '@sentry/react'
import { useLocation } from 'react-router'
import { Plane, Vector3 } from 'three'
import { PCDLoader } from 'three-stdlib'

import { usePolylineSelector } from '../../../store/selector'
import { setTargetColorCursor } from '../../components/TargetColorCursor'
import { CRACK_DETECTION_PCD_ID, MINIMUM_BOUNDARY_PADDING, boundaryThicknessFor } from '../../constants'
import { CrackDetectionStage, DetectionKind } from '../../interfaces'
import { PolylineToolMode, setMode } from '../../store/core'
import {
  WORKING_ID,
  addWorkingPoint,
  clearPointForTargetColor,
  completeDrawing,
  resetCurrent,
  setAreaId,
  setCrackDetectionResult,
  setPointForTargetColor,
  toolChangeCleanup,
  undoLastWorkingPoint,
  updateWorkingPoints,
} from '../../store/crackDetection'
import { addCompletedPolylines } from '../../store/polyline'
import {
  selectCrackDetectionLoadingState,
  selectCrackDetectionStage,
  selectCrackDetectionWorkingPoints,
  selectDetectionKind,
  selectPointForTargetColor,
  selectTargetColorHex,
} from '../../store/selectors'

export default function useMainCanvas(): CanvasConfig {
  const location = useLocation()
  const queries = new URLSearchParams(location.search)
  const inspection_area_id = queries.get('area')

  // Context
  const { sceneRef, setPointProjectionPlane } = useContext(EditorContext)
  const { showErrorModal } = useGlobalModalContext()

  // Store
  const dispatch = useAppDispatch()
  const isAllowedToDebug = useAppSelector((state) => state.user.isAllowedToDebug)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)
  const areaSwitched = useAppSelector((state) => state.page.areaSwitched)

  const selectedTool = useAppSelector((state) => state.editor.selectedTool)

  const editingElementId = useToolUtilSelector((state) => state.editingElementId)
  const hiddenElementIds = useToolUtilSelector((state) => state.hiddenElementIds)
  const disableTools = useToolUtilSelector((state) => state.disableTools)

  const mode = usePolylineSelector((state) => state.core.mode)
  const drawingStage = usePolylineSelector(selectCrackDetectionStage)
  const crackDetectionResult = usePolylineSelector(
    (state) => state.crackDetection.executions[state.crackDetection.areaId],
  )
  const workingPoints = usePolylineSelector(selectCrackDetectionWorkingPoints)
  const isDetectingCrack = usePolylineSelector(selectCrackDetectionLoadingState)
  const detectionKind = usePolylineSelector(selectDetectionKind)
  const pointForTargetColor = usePolylineSelector(selectPointForTargetColor)
  const targetColorHex = usePolylineSelector(selectTargetColorHex)

  // States
  const [isClosing, setIsClosing] = useState(false)
  const [crackPointClouds, setCrackPointClouds] = useState<PointCloudProps[]>([])

  // Refs
  const disableEditingRef = useRef(false)

  // Lets the texture prewarm read the closed polygon without re-running as its vertices move.
  const workingPointsRef = useRef(workingPoints)
  workingPointsRef.current = workingPoints

  // Vars
  const isCrackDetectionMode = mode === PolylineToolMode.CrackDetection
  const isSelectedTool = selectedTool === EDITOR_TOOLS.POLYLINE
  const isWorking = drawingStage === CrackDetectionStage.Drawing
  const isSaving = drawingStage === CrackDetectionStage.SavingPolylines
  const isChalkDetection = detectionKind === DetectionKind.Chalk

  /** The slab this run crops to — the colour pick and the request must agree on it. */
  const boundaryThickness = boundaryThicknessFor(detectionKind)

  // A chalk run needs one point on the mesh to take its target colour from, and asks for it
  // once the polygon is closed. Picking again simply moves the point.
  const isTargetColorStage =
    isChalkDetection && isCrackDetectionMode && isSelectedTool && drawingStage === CrackDetectionStage.Preview
  const isPickingTargetColor = isTargetColorStage && !disableTools

  /** The drawn outline itself, for testing where the colour may be picked from. */
  const drawnArea = useMemo(
    () => (isTargetColorStage && workingPoints.length >= 3 ? workingPoints.map((pt) => new Vector3(...pt)) : undefined),
    [isTargetColorStage, workingPoints],
  )

  /**
   * Whether a point on the mesh is somewhere the target colour may be taken from.
   *
   * Tested against the drawn outline, not a box around it. A box fills in any concavity and
   * reaches out to the corners of a shape that is rarely a rectangle, which would hand the user
   * a margin they can pick in but the detector cannot see.
   *
   * The depth allowance is the slab the detector crops to, so a click on a surface well in front
   * of or behind the drawn area is rejected here rather than failing server-side.
   *
   * @param meshPoint Point on the solid mesh.
   */
  const isWithinDrawnArea = useCallback(
    (meshPoint: PointArray) =>
      Boolean(drawnArea && isPointInPolygon(new Vector3(...meshPoint), drawnArea, boundaryThickness / 2)),
    [drawnArea, boundaryThickness],
  )

  /**
   * Cleanup function to dispose of point cloud geometries and materials
   */
  const cleanup = useCallback(() => {
    setCrackPointClouds((prev) => {
      cleanupPointClouds(prev)
      return []
    })
  }, [])

  /**
   * Handle mouse up event
   */
  const handleMouseUp = useCallback(
    (
      ev: React.MouseEvent<HTMLDivElement, MouseEvent>,
      point: PointArray | undefined,
      solidMeshPoint: PointArray | undefined,
      { isDragged, isDoubleClicked, sampleSolidMeshColor }: CanvasEventsExtra,
    ) => {
      if (isDragged || disableTools) return

      if (isPickingTargetColor) {
        // Mesh hits inside the drawn area only. The detector looks the colour up in a cloud
        // sampled off the textured mesh and cropped to that area, so a point-cloud hit sitting a
        // few centimetres off the surface — or a mesh hit outside the area — finds no colour at
        // all. Such a click is simply ignored.
        if (!solidMeshPoint || !isWithinDrawnArea(solidMeshPoint)) return

        dispatch(setPointForTargetColor({ point: solidMeshPoint, colorHex: sampleSolidMeshColor?.() }))
        return
      }

      const clickedPoint = solidMeshPoint || point
      if (!clickedPoint || drawingStage !== CrackDetectionStage.Drawing) return

      // Double clicked with more than 2 points
      const completesPolygon = workingPoints.length > 2 && isDoubleClicked

      if (completesPolygon) {
        // A hacky way to prevent editing right after completing the polygon
        // needed to prevent polygon modification to continue before state updates
        disableEditingRef.current = true
        setTimeout(() => {
          disableEditingRef.current = false
        }, 500)

        dispatch(completeDrawing())
        setPointProjectionPlane(null)
        return
      }

      dispatch(addWorkingPoint(clickedPoint))

      // If there are 3 points (2 from working, 1 currently added), set the plane for future use
      if (workingPoints.length === 2) {
        const pointsForPlane = workingPoints.concat([clickedPoint]).map((pt) => new Vector3(...pt))
        setPointProjectionPlane(
          new Plane().setFromCoplanarPoints(pointsForPlane[0], pointsForPlane[1], pointsForPlane[2]),
        )
      }
    },
    [
      drawingStage,
      workingPoints,
      disableTools,
      isPickingTargetColor,
      isWithinDrawnArea,
      setPointProjectionPlane,
      dispatch,
    ],
  )

  /**
   * Feed the eyedropper swatch that follows the cursor while a chalk run waits for its colour pick.
   *
   * The bounds check comes before the sample on purpose: reading the texture is the one expensive
   * thing on this path, and outside the drawn area the colour is unpickable anyway.
   */
  const handleMove = useCallback(
    (
      point: PointArray | undefined,
      solidMeshPoint: PointArray | undefined,
      mouseEvent?: React.MouseEvent<HTMLDivElement, MouseEvent>,
      touchEvent?: React.TouchEvent<HTMLDivElement>,
      extra?: CanvasEventsExtra,
    ) => {
      if (!isPickingTargetColor) return

      const isPickable = Boolean(solidMeshPoint && isWithinDrawnArea(solidMeshPoint))
      setTargetColorCursor(isPickable ? extra?.sampleSolidMeshColor?.() : undefined)
    },
    [isPickingTargetColor, isWithinDrawnArea],
  )

  /**
   * Hide the eyedropper swatch as soon as the tool is no longer asking for a colour.
   */
  useEffect(() => {
    if (isPickingTargetColor) return

    setTargetColorCursor(undefined)
  }, [isPickingTargetColor])

  /**
   * Decode the textures under the drawn area the moment the polygon closes.
   *
   * Sampling can only read an already-decoded texture, and the decode is far too slow to do while
   * the pointer is moving. Doing it here means it happens once, during the pause between closing
   * the area and reaching for the chalk mark.
   *
   * Scoped to the tiles the detection itself would use — the same intersection test `handleSubmit`
   * runs to build `mesh_file_names`, padding included, so an edge tile is not missed.
   *
   * Keyed on entering the stage, not on the points: a vertex drag rewrites those on every frame,
   * and re-running this per frame would throw the decoded textures away and decode them again. A
   * drag that reaches a tile this missed just falls back to decoding it on first hover.
   */
  useEffect(() => {
    if (!isTargetColorStage || workingPointsRef.current.length < 3 || !sceneRef.current) return undefined

    const solidMeshManager = sceneRef.current.getObjectByName('solid-mesh-manager')
    if (!solidMeshManager) return undefined

    const bounds = new MinimumBoundingBox(
      workingPointsRef.current.map((pt) => new Vector3(...pt)),
      boundaryThickness,
      MINIMUM_BOUNDARY_PADDING,
    )

    solidMeshManager.traverse((tile) => {
      if (!(tile instanceof MeshTile)) return

      const bbox = getTileWorldBoundingBox(tile, solidMeshManager)
      if (bbox && bounds.intersectsBox(bbox)) prewarmTextureSampling(tile)
    })

    return () => releaseTextureSampling()
  }, [isTargetColorStage, workingPointsRef, sceneRef, boundaryThickness])

  /**
   * DEBUG MODE ONLY
   * When reached `Preview` stage, generate minimum bounding box, and check for intersection with solid mesh.
   */
  useEffect(() => {
    if (
      !isAllowedToDebug ||
      ![CrackDetectionStage.Preview, CrackDetectionStage.Drawing, CrackDetectionStage.None].includes(drawingStage) ||
      !sceneRef.current
    )
      return

    const solidMeshManager = sceneRef.current?.getObjectByName('solid-mesh-manager')
    if (!solidMeshManager) return

    let minBbox: MinimumBoundingBox | undefined
    if (drawingStage === CrackDetectionStage.Preview && workingPoints.length >= 3) {
      // generate minimum bounding box from working points
      minBbox = new MinimumBoundingBox(
        workingPoints.map((pt) => new Vector3(...pt)),
        boundaryThickness,
        MINIMUM_BOUNDARY_PADDING,
      )

      // add minimum bounding box helper to scene
      addWireframeBox(sceneRef.current, 'min-bbox-obb-wireframe', minBbox.getOBB3DCorners(), 0x00ff00)
      addBox3Helper(sceneRef.current, 'min-bbox-aabb-wireframe', minBbox.getAABB(), 0x0000ff)
    } else {
      clearHelper(sceneRef.current, 'min-bbox-obb-wireframe')
      clearHelper(sceneRef.current, 'min-bbox-aabb-wireframe')
    }

    solidMeshManager.traverse((tile) => {
      if (!(tile instanceof MeshTileDebug) || !tile.objFilePath) return

      const bbox = getTileWorldBoundingBox(tile, solidMeshManager)
      const intersecting = Boolean(bbox && minBbox?.intersectsBox(bbox))
      if (drawingStage === CrackDetectionStage.Preview && minBbox) {
        tile.setBoundingBoxColor(intersecting ? 0x00ff00 : 0xff0000, true)
      } else {
        tile.setBoundingBoxColor(0xffff00, false)
      }
    })
  }, [sceneRef, drawingStage, isAllowedToDebug, workingPoints, boundaryThickness])

  /**
   * When crack detection point clouds have been received, load them.
   */
  useEffect(() => {
    // Job token is reset after pcdURL is set, so this will be called twice.
    if (
      !crackDetectionResult?.pcdURL ||
      !crackDetectionResult?.jobToken ||
      crackDetectionResult.status !== CrackDetectionStage.FineTune
    )
      return

    // Clean up without resetting entire crack detection state
    setCrackPointClouds((prev) => {
      cleanupPointClouds(prev)
      return []
    })

    const loadPointClouds = async () => {
      try {
        const loader = new PCDLoader()

        // Load all PCD files concurrently for better performance
        const pcd = await loader.loadAsync(crackDetectionResult.pcdURL!)

        // Ensure the point cloud has geometry and is valid
        if (pcd && pcd.geometry && pcd.geometry.attributes.position) {
          // Create PointCloudProps for the loaded point cloud
          const pointCloudProps: PointCloudProps = {
            id: CRACK_DETECTION_PCD_ID,
            pcd,
          }

          // Automatic polyline extraction
          const points = pointCloudProps.pcd.geometry.attributes.position.array as Float32Array
          const pipeline = new PolylineExtraction({
            pre: [new DensityFilter()],
            extraction: new DbScan(),
            post: [
              // The polylines have a lot of zigzags, so smoothing is necessary
              new Smoothing(),

              new NoiseFilter({
                minEndToEndLength: 0.05,
                minTotalLength: 0.05,
              }),

              // Link, decimate then final noise filter
              new Linking(),
              new Decimate(),
            ],
          })

          const { polylines } = pipeline.extract(points)
          dispatch(
            addCompletedPolylines(
              polylines.map((polyline) => ({
                id: crypto.randomUUID(),
                points: polyline.map((p) => [p.x, p.y, p.z]),
                jobId: crackDetectionResult.id,
              })),
            ),
          )

          setCrackPointClouds([pointCloudProps])
        }
      } catch (error) {
        Sentry.captureException(error)
        dispatch(setCrackDetectionResult({ pcdURL: undefined, pathEntwine: undefined }))
        showErrorModal(
          error instanceof GenericError ? error.message : 'クラック検出中にエラーが発生しました。再試行してください。',
        )
      } finally {
        dispatch(setMode(PolylineToolMode.Draw))
      }
    }

    void loadPointClouds()
  }, [inspectionArea, crackDetectionResult, showErrorModal, dispatch])

  /**
   * When user moves away from the current tool, toggle plane's visibility off.
   */
  useEffect(() => {
    if (crackDetectionResult?.points.length) {
      dispatch(toggleHiddenElementIds({ ids: [WORKING_ID], invisible: !isSelectedTool }))
    }
  }, [isSelectedTool, crackDetectionResult?.points, dispatch])

  /**
   * Reset states, but only if crack detection is not running;
   * 1. When user changes tools (out of Polyline tool)
   * 2. Cleanup point clouds on crackDetection save
   */
  useEffect(() => {
    const handle = () => {
      cleanup()
      dispatch(resetCurrent())
      setPointProjectionPlane(null)
    }
    document.addEventListener('editor:tool:changeReset', handle)
    document.addEventListener('editor:tool:crackDetection:saved', handle)
    return () => {
      document.removeEventListener('editor:tool:changeReset', handle)
      document.removeEventListener('editor:tool:crackDetection:saved', handle)
    }
  }, [cleanup, setPointProjectionPlane, dispatch])

  /**
   * Cleanup point clouds on area switch and component unmount.
   */
  useEffect(() => {
    const handle = () => {
      cleanup()
      dispatch(toolChangeCleanup()) // we don't want to hard-reset on unmount
      setPointProjectionPlane(null)
      dispatch(setMode(PolylineToolMode.Draw))
    }

    if (areaSwitched) {
      handle()
    }

    return handle
  }, [areaSwitched, cleanup, setPointProjectionPlane, dispatch])

  /**
   * Set area ID when inspection area changes
   */
  useEffect(() => {
    if (inspection_area_id) {
      dispatch(setAreaId(inspection_area_id))
    }
  }, [inspection_area_id, dispatch])

  /**
   * Clears point cloud when crack detection enters 'Detecting' stage.
   * This is needed when there are already existing point clouds from previous detection
   * and user re-initiates crack detection.
   */
  useEffect(() => {
    if ([CrackDetectionStage.None, CrackDetectionStage.Polling].includes(drawingStage)) {
      cleanup()
    }
  }, [drawingStage, cleanup])

  return {
    objects: {
      pointClouds: useMemo(
        () =>
          !isSaving
            ? crackPointClouds.map<PointCloudProps>((pcd) => ({
                ...pcd,
                visible: mode === PolylineToolMode.Draw && isSelectedTool && !hiddenElementIds.includes(pcd.id),
              }))
            : [],
        [crackPointClouds, mode, isSelectedTool, isSaving, hiddenElementIds],
      ),
      circleAnchors: [
        /**
         * THE anchor to click to close the polygon.
         * It should be shown on the last point of workingPoints but this is only a placeholder
         * for direct manipulation.
         */
        ...useMemo(
          () =>
            workingPoints.length > 1 && isWorking
              ? [
                  {
                    id: 'working-crack-detection-polygon-complete-anchor',
                    point: workingPoints[0] || [0, 0, 0],
                    outlineOpacity: 0,
                    color: isClosing ? '#f00' : '#248CE4',
                    scale: 1.3,
                    icon: CircleAnchorIcon.CHECK,
                    iconScale: 1.1,
                    iconColor: 'white',
                    tooltipText: isClosing ? 'この延長の作成を完了' : undefined,
                    onEnter: (event: ThreeEvent<PointerEvent>) => {
                      event.stopPropagation()
                      dispatch(setCursor(CursorState.POINTER))
                      setIsClosing(true)
                    },
                    onLeave: (event: ThreeEvent<PointerEvent>) => {
                      event.stopPropagation()
                      dispatch(setCursor(CursorState.NONE))
                      setIsClosing(false)
                    },
                    onUp: (event: ThreeEvent<PointerEvent>) => {
                      event.stopPropagation()
                      if (workingPoints.length > 1) {
                        dispatch(completeDrawing())

                        // Same as closing by double-click. Leaving the plane set makes every later
                        // hit get projected onto it, so the chalk pick would record a point off the
                        // mesh surface and its depth check would compare against itself.
                        setPointProjectionPlane(null)
                      }
                    },
                  },
                ]
              : [],
          [workingPoints, isClosing, isWorking, setPointProjectionPlane, dispatch],
        ),

        /**
         * Chalk detection's colour pick, painted in the colour it sampled so the user can tell at
         * a glance what they hit. Not interactable — clicking it again moves the pick.
         *
         * Tied to the pick stage, so it goes away with the tool (like the drawn area does) and
         * once the detection comes back, where it would only clutter the returned lines.
         */
        ...useMemo(
          () =>
            isTargetColorStage && pointForTargetColor
              ? [
                  {
                    id: 'working-chalk-detection-target-color-anchor',
                    point: pointForTargetColor,
                    color: targetColorHex || '#248CE4',
                    outlineColor: 'white',
                    outlineOpacity: 1,
                    scale: 1.1,
                    innerScale: 0.6,
                    isInteractable: false,
                    depthTest: false,
                    depthWrite: false,
                    renderOrder: 1001,
                  },
                ]
              : [],
          [isTargetColorStage, pointForTargetColor, targetColorHex],
        ),
      ],
      polygonPlaneMeshesV2: [
        /**
         * Working plane
         */
        ...useMemo((): PolygonPlaneMeshProps[] => {
          if (!workingPoints.length || isSaving) return []

          // Generate vertices
          const center = findCenter(workingPoints.map((v) => new Vector3(...v)))!
          const alignedPoints = alignToZAxis(workingPoints.map((v) => new Vector3(...v)))
          const vertices = alignedPoints.points.map((v) => [v.x, v.y] as [number, number])
          const isEditing = editingElementId === WORKING_ID && !isWorking

          return [
            {
              id: WORKING_ID,
              polygon: {
                shape_id: WORKING_ID,
                positions: workingPoints,
                vertices,
                center: center.toArray(),
                transformation: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
                min_rect_boundaries: generateDummyMinimumRectBoundary(vertices),
              } as Polygon,
              transparent: true,
              color: '#13CD25',
              guideLine: {
                color: 'white',
                thickness: 0.002,
                style: drawingStage === CrackDetectionStage.Drawing ? LineStyle.Dashed : LineStyle.Solid,
              },
              guideLabel: {
                visible: mode === PolylineToolMode.CrackDetection,
              },
              label: {
                visible: workingPoints.length >= 2 && mode === PolylineToolMode.CrackDetection, // should be 3 but the 3rd is handled by PolygonMesh while in working state
                generateLabel: (points: PointArray[]) => {
                  if (points.length < 3) return ''

                  // calculate 2D bbox area
                  const obb = new MinimumBoundingBox(points.map((pt) => new Vector3(pt[0], pt[1], pt[2])))
                  return `面積: ${obb.getOBB2D().area.toFixed(2)}m²`
                },
              },
              vertexAnchors: {
                visible: !isWorking && !isDetectingCrack && isCrackDetectionMode,
                depthTest: false,
                depthWrite: false,
                onUpdated: (positions: PointArray[]) => {
                  if (!isEditing) return
                  dispatch(updateWorkingPoints(positions))
                },
              },
              opacity: isCrackDetectionMode ? 0.3 : 0.1,
              visible: !hiddenElementIds.includes(WORKING_ID),
              selectable: false,
              isOpenLoop: true,
              isWorking,
              isEditing,
              depthTest: false,
              depthWrite: false,
              renderOrder: 1000,
              disableEditingRef,
            },
          ]
        }, [
          mode,
          isSaving,
          editingElementId,
          hiddenElementIds,
          workingPoints,
          drawingStage,
          isWorking,
          isDetectingCrack,
          isCrackDetectionMode,
          dispatch,
        ]),
      ],
    },
    events: isSelectedTool
      ? {
          onMouseUp: handleMouseUp,
          onMove: handleMove,
          onUndo: () => {
            // On a chalk run the colour pick is the most recent thing the user did, so it undoes
            // first — leaving them back at the completed polygon rather than mid-drawing.
            if (isChalkDetection && pointForTargetColor) {
              dispatch(clearPointForTargetColor())
              return
            }

            // reset point selection plane when undoing last point if there are only 3 points
            if (workingPoints.length === 3) {
              setPointProjectionPlane(null)
            }

            dispatch(undoLastWorkingPoint())
          },
        }
      : undefined,
  }
}

/**
 * Cleanup function to dispose of point cloud geometries and materials
 */
const cleanupPointClouds = (crackPointClouds: PointCloudProps[]) => {
  crackPointClouds.forEach((pointCloud) => {
    if (pointCloud.pcd) {
      // Dispose geometry
      if (pointCloud.pcd.geometry) {
        pointCloud.pcd.geometry.dispose()
      }

      // Dispose material
      if (pointCloud.pcd.material) {
        if (Array.isArray(pointCloud.pcd.material)) {
          pointCloud.pcd.material.forEach((material) => material.dispose())
        } else {
          pointCloud.pcd.material.dispose()
        }
      }
    }
  })
}
